import React, { useState, useEffect } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  useLevels,
  useCreateLevel,
  useUpdateLevel,
  useDeleteLevel,
} from "../hooks/useLevels";

function LevelModal({ show, onHide, editing }) {
  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const createMutation = useCreateLevel();
  const updateMutation = useUpdateLevel();
  const isEdit = Boolean(editing);
  const mutation = isEdit ? updateMutation : createMutation;

  useEffect(() => {
    setName(isEdit ? editing.name ?? "" : "");
    setAbbreviation(isEdit ? editing.abbreviation ?? "" : "");
  }, [editing, show, isEdit]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = isEdit
      ? { id: editing.id, name, abbreviation }
      : { name, abbreviation };
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(isEdit ? "Nivel actualizado" : "Nivel creado");
        onHide();
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.detail ??
            (isEdit ? "No se pudo actualizar el nivel." : "No se pudo crear el nivel.")
        ),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Editar nivel" : "Nuevo nivel"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nombre *</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. B1 - Intermedio"
              required
              style={{ minHeight: 44 }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Abreviatura</Form.Label>
            <Form.Control
              value={abbreviation}
              onChange={(e) => setAbbreviation(e.target.value)}
              placeholder="Ej. B1"
              maxLength={50}
              style={{ minHeight: 44 }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide} style={{ minHeight: 44 }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending || !name.trim()}
            style={{ minHeight: 44 }}
          >
            {mutation.isPending ? <Spinner size="sm" /> : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function NivelesPage() {
  const { data: levels = [], isLoading, isError } = useLevels();
  const deleteMutation = useDeleteLevel();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => {
    setEditing(null);
    setShowModal(true);
  };
  const openEdit = (lvl) => {
    setEditing(lvl);
    setShowModal(true);
  };

  const handleDelete = (lvl) => {
    if (
      !window.confirm(
        `¿Eliminar el nivel "${lvl.name}"? Los estudiantes en este nivel pasarán al nivel más bajo restante.`
      )
    )
      return;
    deleteMutation.mutate(lvl.id, {
      onSuccess: () => toast.success("Nivel eliminado"),
      onError: () => toast.error("No se pudo eliminar el nivel."),
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Niveles</h4>
          <p className="text-muted small mb-0">
            Los niveles van de menor a mayor. Un estudiante sin nivel asignado
            arranca en el más bajo. La academia evalúa (por fuera de la app) y
            asigna el nivel desde la clase o la sección de estudiantes.
          </p>
        </div>
        <Button variant="primary" onClick={openNew} style={{ minHeight: 44 }}>
          + Nuevo nivel
        </Button>
      </div>

      {isLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {isError && (
        <Alert variant="danger">No se pudieron cargar los niveles.</Alert>
      )}
      {!isLoading && !isError && levels.length === 0 && (
        <Card>
          <p className="text-muted text-center py-4 mb-0">
            Aún no hay niveles. Crea el primero.
          </p>
        </Card>
      )}

      {levels.length > 0 && (
        <>
          {/* PC: tabla */}
          <Card className="d-none d-md-block">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Orden</th>
                    <th>Nivel</th>
                    <th style={{ width: 120 }}>Abreviatura</th>
                    <th style={{ width: 180 }} className="text-end pe-3">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {levels.map((lvl, i) => (
                    <tr key={lvl.id}>
                      <td>
                        <Badge bg={i === 0 ? "success" : "secondary"}>
                          {i === 0 ? "más bajo" : lvl.order}
                        </Badge>
                      </td>
                      <td className="fw-medium">{lvl.name}</td>
                      <td>{lvl.abbreviation || "—"}</td>
                      <td className="text-end pe-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openEdit(lvl)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(lvl)}
                          disabled={deleteMutation.isPending}
                        >
                          Eliminar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Móvil: tarjetas */}
          <div className="d-md-none d-flex flex-column gap-2">
            {levels.map((lvl, i) => (
              <Card key={lvl.id}>
                <Card.Body className="py-2 px-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Badge bg={i === 0 ? "success" : "secondary"}>
                      {i === 0 ? "más bajo" : lvl.order}
                    </Badge>
                    <span className="fw-medium">{lvl.name}</span>
                  </div>
                  <div className="text-muted small mb-2">
                    Abreviatura: {lvl.abbreviation || "—"}
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="flex-fill"
                      style={{ minHeight: 44 }}
                      onClick={() => openEdit(lvl)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="flex-fill"
                      style={{ minHeight: 44 }}
                      onClick={() => handleDelete(lvl)}
                      disabled={deleteMutation.isPending}
                    >
                      Eliminar
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </>
      )}

      <LevelModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editing={editing}
      />
    </div>
  );
}

export { NivelesPage };
